/**
 * Follows redirects and returns the final URL after all server-side redirects
 * This method opens the URL in a new window/tab to detect redirects, then closes it
 * @param {string} baseUrl - The base URL to check
 * @param {number} timeout - Timeout in milliseconds (default: 3000)
 * @returns {Promise<string>} - Final URL after redirects
 */
export async function getFinalUrlAfterRedirects(baseUrl, timeout = 3000) {
  try {
    // Ensure URL has protocol
    const prefix = baseUrl.startsWith('http') ? '' : 'https://';
    const fullUrl = `${prefix}${baseUrl}`;

    // Try modern fetch approach first (works in most browsers)
    try {
      const finalUrl = await getFinalUrlViaFetch(fullUrl, timeout);
      if (finalUrl && finalUrl !== fullUrl) {
        return finalUrl;
      }
    } catch (fetchError) {
      // eslint-disable-next-line no-console
      console.log('Fetch method failed, trying popup method:', fetchError);
    }

    // Fallback: Open temporary window to detect final URL
    // This is the most reliable cross-browser method
    return await getFinalUrlViaWindow(fullUrl, timeout);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to get final URL, using base URL:', error);
    // Return original URL on any error
    const prefix = baseUrl.startsWith('http') ? '' : 'https://';
    return `${prefix}${baseUrl}`;
  }
}

/**
 * Try to detect final URL using fetch (modern browsers)
 * @param {string} url - URL to check
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<string>} - Final URL or null if failed
 */
async function getFinalUrlViaFetch(url, timeout) {
  // Check if AbortController is supported
  const useAbort = typeof AbortController !== 'undefined';
  const controller = useAbort ? new AbortController() : null;
  const timeoutId = useAbort ? setTimeout(() => controller.abort(), timeout) : null;

  try {
    const fetchOptions = {
      method: 'HEAD',
      redirect: 'follow',
      credentials: 'include', // Include cookies
    };

    // Add signal only if AbortController is supported
    if (useAbort && controller) {
      fetchOptions.signal = controller.signal;
    }

    const response = await fetch(url, fetchOptions);

    if (timeoutId) clearTimeout(timeoutId);

    // Check if we got a valid response with URL
    // Note: response.url might be empty string with no-cors mode
    if (response.url && response.url !== url && response.url !== '') {
      return response.url;
    }

    return null;
  } catch (error) {
    if (timeoutId) clearTimeout(timeoutId);
    throw error;
  }
}

/**
 * Most reliable method: open window, detect URL, close it
 * Works in all browsers including Safari, IE11, etc.
 * @param {string} url - URL to check
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<string>} - Final URL
 */
function getFinalUrlViaWindow(url, timeout) {
  return new Promise((resolve) => {
    // Open in a small, hidden-ish window
    const features = 'width=1,height=1,left=10000,top=10000,menubar=no,toolbar=no,location=no';
    const popup = window.open(url, '_blank', features);

    if (!popup) {
      // Popup blocked, return original URL
      // eslint-disable-next-line no-console
      console.warn('Popup blocked, using original URL');
      resolve(url);
      return;
    }

    let resolved = false;
    let checkCount = 0;
    const maxChecks = Math.floor(timeout / 100); // Check every 100ms

    // Try to read URL after page loads
    const checkUrl = setInterval(() => {
      checkCount++;

      try {
        // Try to access popup location
        if (popup.location && popup.location.href && popup.location.href !== 'about:blank') {
          resolved = true;
          clearInterval(checkUrl);
          const finalUrl = popup.location.href;
          popup.close();
          resolve(finalUrl);
          return;
        }
      } catch (e) {
        // Cross-origin error - page has loaded but we can't access it
        // This actually means redirect happened to different domain
        // We can't get the URL due to security, so we'll use original
      }

      // Timeout reached
      if (checkCount >= maxChecks) {
        resolved = true;
        clearInterval(checkUrl);
        popup.close();
        resolve(url); // Return original URL
      }
    }, 100);

    // Backup timeout
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        clearInterval(checkUrl);
        try {
          popup.close();
        } catch (e) {
          // Ignore close errors
        }
        resolve(url);
      }
    }, timeout + 500);
  });
}

/**
 * Adds preview parameter to URL
 * @param {string} url - Base URL
 * @param {string} paramName - Parameter name (default: 'kea-preview')
 * @param {string} paramValue - Parameter value (default: 'true')
 * @returns {string} - URL with preview parameter
 */
export function addPreviewParameter(url, paramName = 'kea-preview', paramValue = 'true') {
  try {
    const urlObj = new URL(url);
    urlObj.searchParams.set(paramName, paramValue);
    return urlObj.toString();
  } catch (e) {
    // Fallback for invalid URLs
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${paramName}=${paramValue}`;
  }
}

/**
 * Get final URL after redirects via Bridge (for embedded mode)
 * @param {string} baseUrl - Base URL to check
 * @param {Function} bridgeGetFinalUrlFn - Bridge function to call
 * @returns {Promise<string>} - Final URL
 */
export async function getFinalUrlViaBridge(baseUrl, bridgeGetFinalUrlFn) {
  try {
    const prefix = baseUrl.startsWith('http') ? '' : 'https://';
    const fullUrl = `${prefix}${baseUrl}`;

    // Call bridge method which will be handled on the parent window
    const response = await bridgeGetFinalUrlFn(fullUrl);

    if (response && response.finalUrl) {
      return response.finalUrl;
    }

    // Fallback to original URL if bridge doesn't return finalUrl
    return fullUrl;
  } catch (error) {
    console.warn('Bridge getFinalUrl failed, using base URL:', error);
    const prefix = baseUrl.startsWith('http') ? '' : 'https://';
    return `${prefix}${baseUrl}`;
  }
}
